import {
  FormikContextTypeEnhanced,
  useFormikEnhanced,
} from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { noop } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { APIQueryResult } from 'shared/api/APIQuery';
import { trackLoadboardEvent } from '../../LoadboardAnalytics';
import {
  mapPayloadToValues,
  mapValuesToPayload,
  NotificationSettingsValuesDTO,
  notificationSettingsValuesSchema,
  NotificationsSettingDTO,
  notificationsSettingPayloadSchema,
} from './LoadboardNotificationsSettingDTO';
import {
  useNotificationsSettings,
  useUpdateNotificationsSettings,
  useUserSettings,
} from './LoadboardSettingsAPI';
import { LoadboardSubscriptionSettingsDTO } from './LoadboardUserSettingsDTO';

export interface NotificationsSettingsFormResult {
  isLoading: boolean;
  hasDisabled: boolean | undefined;

  userSettings: APIQueryResult<LoadboardSubscriptionSettingsDTO>;
  notificationSettings: APIQueryResult<NotificationsSettingDTO[]>;

  settingsForm: FormikContextTypeEnhanced<NotificationSettingsValuesDTO, void>;
  allNotificationForm: FormikContextTypeEnhanced<Record<string, unknown>, void>;
}

export function useNotificationsSettingsForm(): NotificationsSettingsFormResult {
  const { addSnackbar } = useSnackbarStack();

  const userSettings = useUserSettings();
  const notificationSettings = useNotificationsSettings();

  const { mutateAsync: updateNotificationSettings } =
    useUpdateNotificationsSettings();

  const hasDisabled = useMemo(
    () => notificationSettings.data?.some(({ active }) => !active),
    [notificationSettings.data],
  );

  const initialValues = useMemo<NotificationSettingsValuesDTO>(() => {
    if (!notificationSettings.data) {
      return notificationSettingsValuesSchema.cast({
        load_suggested: { email_enabled: false, sms_enabled: false },
        saved_search: {
          email_enabled: false,
          sms_enabled: false,
          push_enabled: false,
        },
        request_declined: {
          email_enabled: false,
          sms_enabled: false,
          push_enabled: false,
        },
      });
    }

    return mapPayloadToValues(notificationSettings.data);
  }, [notificationSettings.data]);

  const allNotificationForm = useFormikEnhanced({
    initialValues: {},
    onSubmit() {
      const toggledNotificationSettings = notificationSettings.data?.map(
        ({ activity }) => ({
          activity,
          active: hasDisabled,
        }),
      );

      if (!toggledNotificationSettings) {
        return Promise.resolve();
      }

      const payload = toggledNotificationSettings.map((x) =>
        notificationsSettingPayloadSchema.cast(x),
      );

      return updateNotificationSettings(payload).then(() => {
        trackLoadboardEvent({
          name: 'SLB: All notification settings updated',
          updatedNotificationSettings: payload,
        });
      });
    },
    onSubmitSuccess() {
      void notificationSettings.refetch();
      addSnackbar('Notifications updated successfully', { variant: 'success' });
    },
    onSubmitFailure() {
      void notificationSettings.refetch();
      addSnackbar('Something went wrong', { variant: 'error' });
    },
  });

  const settingsForm = useFormikEnhanced<NotificationSettingsValuesDTO, void>({
    initialValues,
    onSubmit(values) {
      const payload = mapValuesToPayload(values);
      return updateNotificationSettings(payload).then(noop);
    },
    onSubmitSuccess(_, values) {
      trackLoadboardEvent({
        name: 'SLB: Notification settings updated',
        notificationSettings: initialValues,
        updatedNotificationSettings: values,
      });
      addSnackbar('Notifications updated successfully', { variant: 'success' });
      void notificationSettings.refetch();
    },
    onSubmitFailure() {
      void notificationSettings.refetch();
      addSnackbar('Something went wrong', { variant: 'error' });
    },
  });

  return useMemo(
    () => ({
      hasDisabled,
      userSettings,
      settingsForm,
      allNotificationForm,
      notificationSettings,
      isLoading:
        notificationSettings.isLoading ||
        settingsForm.isSubmitting ||
        allNotificationForm.isSubmitting,
    }),
    [
      userSettings,
      hasDisabled,
      settingsForm,
      notificationSettings,
      allNotificationForm,
    ],
  );
}
